import {
    collection,
    doc,
    getDocs,
    query,
    where,
    orderBy,
    getDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";

// ============================================================================
// CLIENT-SIDE CACHING UTILITY
// ============================================================================

const getCachedActiveBanners = async (storeId) => {
    const cacheKey = `activeBanners_${storeId}`;
    const cached = sessionStorage.getItem(cacheKey);
    const cachedTs = sessionStorage.getItem(`${cacheKey}_ts`);

    // 5 Minute Cache (Banners change infrequently)
    if (cached && cachedTs && (Date.now() - parseInt(cachedTs) < 5 * 60 * 1000)) {
        return JSON.parse(cached);
    }

    // Fetch Fresh from DB
    const q = query(
        collection(db, "stores", storeId, "announcementBanners"),
        where("isActive", "==", true),
        orderBy("displayOrder", "asc"),
        orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const banners = [];

    querySnapshot.forEach((doc) => {
        const data = doc.data();
        banners.push({
            id: doc.id,
            ...data,
            // Store dates as ISO strings for JSON compatibility
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : new Date().toISOString()
        });
    });

    // Save to Cache
    sessionStorage.setItem(cacheKey, JSON.stringify(banners));
    sessionStorage.setItem(`${cacheKey}_ts`, Date.now().toString());

    return banners;
};

// ============================================================================
// ANNOUNCEMENT BANNERS FUNCTIONS
// ============================================================================

/**
 * Get active announcement banners (Client-side optimized)
 */
export const getActiveAnnouncementBanners = async (storeId) => {
    try {
        return await getCachedActiveBanners(storeId);
    } catch (error) {
        console.error("Error getting active announcement banners:", error);
        throw error;
    }
};

/**
 * Create a new announcement banner (SECURE - Server-side)
 */
export const createAnnouncementBanner = async (storeId, bannerData) => {
    try {
        if (!storeId) throw new Error("Store ID is required");

        const fn = httpsCallable(functions, 'createAnnouncementBanner');
        const result = await fn({ storeId, bannerData });

        if (result.data.success) {
            // Invalidate cache on write
            sessionStorage.removeItem(`activeBanners_${storeId}`);
            return result.data.bannerId;
        } else {
            throw new Error(result.data.message || 'Failed to create banner');
        }
    } catch (error) {
        console.error("Error creating announcement banner:", error);
        throw error;
    }
};

/**
 * Get all announcement banners for a store (Admin - No cache)
 */
export const getAnnouncementBanners = async (storeId, activeOnly = false) => {
    try {
        let q = query(
            collection(db, "stores", storeId, "announcementBanners"),
            orderBy("displayOrder", "asc"),
            orderBy("createdAt", "desc")
        );

        if (activeOnly) {
            q = query(
                collection(db, "stores", storeId, "announcementBanners"),
                where("isActive", "==", true),
                orderBy("displayOrder", "asc"),
                orderBy("createdAt", "desc")
            );
        }

        const querySnapshot = await getDocs(q);
        const banners = [];

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            banners.push({
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
                updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
            });
        });

        return banners;
    } catch (error) {
        console.error("Error getting announcement banners:", error);
        throw error;
    }
};

/**
 * Update an announcement banner (SECURE - Server-side)
 */
export const updateAnnouncementBanner = async (storeId, bannerId, updateData) => {
    try {
        const fn = httpsCallable(functions, 'updateAnnouncementBanner');
        const result = await fn({ storeId, bannerId, bannerData: updateData });

        if (result.data.success) {
            sessionStorage.removeItem(`activeBanners_${storeId}`); // Invalidate Cache
            return true;
        } else {
            throw new Error(result.data.message);
        }
    } catch (error) {
        console.error("Error updating announcement banner:", error);
        throw error;
    }
};

/**
 * Delete an announcement banner (SECURE - Server-side)
 */
export const deleteAnnouncementBanner = async (storeId, bannerId) => {
    try {
        const fn = httpsCallable(functions, 'deleteAnnouncementBanner');
        const result = await fn({ storeId, bannerId });

        if (result.data.success) {
            sessionStorage.removeItem(`activeBanners_${storeId}`); // Invalidate Cache
            return true;
        } else {
            throw new Error(result.data.message);
        }
    } catch (error) {
        console.error("Error deleting announcement banner:", error);
        throw error;
    }
};

export const toggleAnnouncementBannerStatus = async (storeId, bannerId, isActive) => {
    return await updateAnnouncementBanner(storeId, bannerId, { isActive });
};

export const reorderAnnouncementBanners = async (storeId, bannerOrders) => {
    try {
        const promises = bannerOrders.map(({ id, displayOrder }) => {
            return updateAnnouncementBanner(storeId, id, { displayOrder });
        });

        await Promise.all(promises);
        return true;
    } catch (error) {
        console.error("Error reordering announcement banners:", error);
        throw error;
    }
};

export const getAnnouncementSettings = async (storeId) => {
    try {
        const storeDoc = await getDoc(doc(db, "stores", storeId));
        if (!storeDoc.exists()) throw new Error("Store not found");

        const data = storeDoc.data();
        return data.announcementSettings || { enabled: false, message: "" };
    } catch (error) {
        console.error("Error getting announcement settings:", error);
        throw error;
    }
};

export const updateAnnouncementSettings = async (storeId, announcementSettings) => {
    try {
        const fn = httpsCallable(functions, 'updateAnnouncementSettings');
        const result = await fn({ storeId, announcementSettings });
        if (result.data.success) {
            return true;
        } else {
            throw new Error(result.data.message);
        }
    } catch (error) {
        console.error("Error updating announcement settings:", error);
        throw error;
    }
};

// ============================================================================
// MULTILINGUAL HELPER FUNCTIONS (Memory Only - No DB Calls)
// ============================================================================

export const getLocalizedBanner = (banner, language = 'en') => {
    if (!banner) return null;
    const localizedBanner = { ...banner };
    if (banner.multilingual && banner.multilingual[language]) {
        localizedBanner.text = banner.multilingual[language].text || banner.text;
    }
    return localizedBanner;
};

export const getLocalizedBanners = (banners, language = 'en') => {
    return banners.map(banner => getLocalizedBanner(banner, language));
};

